<template>
  <div
    aria-live="polite"
    role="region"
    class="product-options"
    :class="{ [$class('loading')]: loading }"
  >
    <fieldset
      v-for="(option, index) of options"
      :key="getOptionKey(option)"
      class="product-options__option"
      :class="{
        [$class('hidden')]: isOptionHidden(option),
      }"
    >
      <legend
        class="visually-hidden"
        v-text="getOptionLegend(option)"
      />

      <div class="product-options__option-title-container">
        <span
          aria-hidden="true"
          role="presentation"
          class="product-options__option-title text-body-s-regular"
          v-html="getOptionLabel(option)"
        />

        <transition name="fade">
          <span
            v-if="missingOptionNames.includes(option.name) && displayMissingOptions"
            class="text-body-xs-regular product-options__option-title--error"
            v-text="getMissingOptionPrompt(option)"
          />
        </transition>

        <!-- Size guide - right aligned -->
        <slot
          v-if="sizeGuideIndex === index && (sizeGuidePosition !== 'below' || isMobile)"
          name="sizeGuide"
        />
      </div>

      <div class="product-options__values">
        <selection-tab
          v-for="(value, valueIndex) of option.values"
          :id="getOptionValueKey(value, valueIndex, option)"
          :key="getOptionValueKey(value, valueIndex, option)"
          v-model="currentOptions[option.index]"
          class="product-options__value"
          :disabled="getValueDisabled(option.disabled[value], 'disabled')"
          :label="getOptionValueLabel(option, value)"
          :name="getOptionKey(option)"
          :value="value"
          :visually-disabled="getValueDisabled(option.disabled[value], 'visually-disabled')"
        />
      </div>

      <!-- Size guide - bottom aligned (desktop only) -->
      <slot
        v-if="sizeGuideIndex === index && sizeGuidePosition === 'below' && !isMobile"
        name="sizeGuide"
      />
    </fieldset>

    <select
      v-model="currentVariantId"
      class="product-options__variant-select"
      name="id"
    >
      <option
        v-for="(variantObject, index) of product.variants"
        :key="getVariantKey(variantObject, index)"
        :value="variantObject.id"
        v-text="`${variantObject.title} - ${variantObject.id}`"
      />
    </select>
  </div>
</template>

<script>
/**
 * Vue: Product options (product-form)
 * -----------------------------------------------------------------------------
 * Product variants split into separate options and values.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 * @emits updateVariant - Emit new variant object.
 * @emits optionsSelected - Options were selected.
 *
 */
import { mapState } from 'vuex'

import SelectionTab from '~async/utils/selection-tab/selection-tab'

import { convertToHandle } from '~/helpers/convert'
import { generateUUID } from '~/helpers/general'
import { getSortedOptionValues, isBisOverride } from '~/helpers/product'
import { emitUpdateVariant } from '~/helpers/vue'

export default {
  name: 'ProductOptions',

  components: {
    SelectionTab,
  },

  props: {
    displayMissingOptions: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        autoOpenCartDrawer: true,
        excludedInitialSelectOptions: [],
        initialSelect: false,
        resetFormBehaviour: 'disabled',
        selectDisabledValues: true,
      }),
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    isBis: {
      type: Boolean,
      default: false,
    },
    sizeGuidePosition: {
      type: String,
      default: 'right',
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
    'options-selected': () => true,
  },

  data() {
    return {
      currentVariantId: 0,
      currentOptions: [],
      options: [],
      namespace: generateUUID(),
    }
  },

  computed: {
    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute option names that require selections
     * @returns {*[]}
     */
    missingOptionNames() {
      return this.options
        .filter((option) => {
          return !this.currentOptions[option.index]
        })
        .map((option) => option.name)
    },

    /**
     * Compute option index that should show the size guide button. Defaults to
     * the last index if no option names with the word "size" exists.
     * @returns {number}
     */
    sizeGuideIndex() {
      const index = this.options.findIndex((option) => option.name.toLowerCase().includes('size'))
      return index < 0 ? this.options.length - 1 : index
    },

    /**
     * Compute scarcity message based on selected variant
     * @returns {string}
     */
    scarcityMessage() {
      const { inventory, available } = this.variant || { available: false, inventory: 0 }
      const { title } = this.product || { title: '' }
      if (
        inventory > 4 ||
        inventory < 1 ||
        !available ||
        title.toLowerCase().includes('artisan')
      ) {
        return ''
      }
      return this.$string('product.form.scarcity_message', {
        replace: {
          quantity: inventory > 1 ? 'a few' : 'one',
        },
      })
    },
  },

  watch: {
    /**
     * Watch current options to update based on option choices.
     */
    currentOptions: {
      handler() {
        this.getOptions()
        const variantTitle = this.currentOptions.join(' / ')
        const variant = this.getVariantFromProperty(variantTitle, 'title')

        if (!variant) {
          return
        }

        this.currentVariantId = variant.id
      },
      deep: true,
    },

    /**
     * Watch currentVariantId to update Canvas variant when select changes.
     */
    currentVariantId() {
      if (this.variant.id === this.currentVariantId) {
        return
      }

      const variant = this.getVariantFromProperty(this.currentVariantId)

      if (!variant) {
        return
      }

      this.$emit('update-variant', variant)
    },

    /**
     * Watch product to update selected variant when product data is loaded.
     */
    product() {
      if (this.currentOptions.length) {
        this.transitionCurrentOptions()
      } else {
        this.getCurrentVariant()
      }
    },

    /**
     * Watch variant for updates to update current options.
     */
    variant() {
      if (!this.variant) {
        this.currentVariantId = 0
      }

      this.getCurrentOptions()
    },

    /**
     * Watch missing names and auto select single options
     */
    missingOptionNames() {
      if (!this.product) {
        return
      }

      const singleOptions = this.product.options.filter((option) => {
        return this.missingOptionNames.includes(option.name) && option.values?.length === 1
      })

      singleOptions.forEach((option) => {
        this.currentOptions[option.index] = option.values[0]
      })
    },
  },

  mounted() {
    this.getCurrentVariant()
    this.getOptions()
  },

  methods: {
    /**
     * Get the prompt message that appears next to the options label
     * if the form CTA was clicked and no variant was selected.
     * @param option
     * @returns {string}
     */
    getMissingOptionPrompt(option) {
      if (this.isBis) {
        return this.$string('back_in_stock.product_form.select_option', {
          replace: {
            option_name: option.name.toLowerCase(),
          },
        })
      }
      return this.$string('product.form.select_option', {
        replace: {
          option_name: option.name.toLowerCase(),
        },
      })
    },

    /**
     * Get option key.
     * @param {Object} option - Option object.
     * @returns {String}
     */
    getOptionKey(option) {
      return `${this.product.handle}-option-${option.index}-${convertToHandle(option.name)}-${this.namespace}`
    },

    /**
     * Get option value key/id.
     * @param {String} value - Value name.
     * @param {Number} index - Forloop index.
     * @param {Object} option - Option object.
     * @returns {String}
     */
    getOptionValueKey(value, index, option) {
      return `${this.getOptionKey(option)}-value-${index}-${convertToHandle(value)}`
    },

    /**
     * Get variant key.
     * @param {Object} variant - Variant object.
     * @param {Number} index - Forloop index.
     * @returns {String}
     */
    getVariantKey(variant, index) {
      return `${this.product.handle}-option-${index}-${convertToHandle(variant.title)}-${this.namespace}`
    },

    /**
     * Get if value is disabled.
     * - Based on section setting and attribute.
     * @param {Boolean} available - Value availability.
     * @param {String} attribute - Seleection tab attribute.
     * @returns {Boolean}
     */
    getValueDisabled(available, attribute) {
      if (!available) {
        return false
      }

      return (
        (attribute === 'disabled' && !this.section.selectDisabledValues) ||
        (attribute === 'visually-disabled' && this.section.selectDisabledValues)
      )
    },

    /**
     * Get current variant on page load or product update.
     */
    getCurrentVariant() {
      if (!this.product || !this.product?.variants?.length) {
        return
      }

      if (cnvs.storybook && this.variant) {
        this.getCurrentOptions()
        return
      }

      /**
       * Always get current variant from URL.
       */
      const variantFromUrl = this.getVariantFromUrl()

      if (variantFromUrl) {
        this.currentVariantId = variantFromUrl.id
        return
      }

      /**
       * Do not set current variant if disabled in settings.
       */
      if (!this.section.initialSelect) {
        this.getOptions()
        return
      }

      /**
       * Pick the first in stock variant.
       * - If no variants are in stock then use first variant.
       */
      const inStock = this.product.variants.filter((variant) => variant.available)
      const currentVariant = inStock.length ? inStock[0] : this.product.variants[0]

      /**
       * Only set current variant if none of the variant options are excluded
       * from being initially set.
       */
      const currentOptions = currentVariant.selectedOptions.map((selectedOption) => {
        const optionHandle = convertToHandle(selectedOption.name)

        return this.section.excludedInitialSelectOptions.includes(optionHandle)
          ? ''
          : selectedOption.value
      })

      const filteredCurrentOptions = currentOptions.filter(Boolean)

      if (filteredCurrentOptions.length !== currentVariant.selectedOptions.length) {
        this.currentOptions = currentOptions
        return
      }

      this.currentVariantId = currentVariant.id
      this.getCurrentOptions()
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {Object}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1))
      const variantId = Number(params.get('variant'))
      return this.getVariantFromProperty(variantId)
    },

    /**
     * Get variant object from variant property.
     * @param {String} value - Value to match.
     * @param {String} property - Property to find by.
     * @returns {Object}
     */
    getVariantFromProperty(value, property = 'id') {
      return this.product.variants?.find((variant) => {
        return variant[property] === value
      })
    },

    /**
     * Get current options.
     */
    getCurrentOptions() {
      if (!this.variant) {
        this.transitionCurrentOptions()
        return
      }

      this.variant.selectedOptions.forEach((selectedOption, index) => {
        this.currentOptions[index] = selectedOption.value
      })

      this.$emit('options-selected', this.currentOptions)
    },

    /**
     * Get extended options data.
     * - Extends product data with disabled data.
     * @returns {Array}
     */
    getOptions() {
      if (!this.product?.options?.length) {
        this.options = []
        return
      }

      this.options = this.product.options.map((option, optionIndex) => {
        const disabled = {}

        /**
         * Compute if option is unavailable.
         * - First find variants that match current value.
         * - Then find variants that match other currentOptions.
         * - Then test to see if all matching variants are unavailable.
         */
        option.values.forEach((value) => {
          if (isBisOverride(this.product)) {
            disabled[value] = true
            return
          }

          disabled[value] = this.product.variants
            .filter((variant) => variant.selectedOptions[optionIndex].value === value)
            .filter((variant) => {
              return this.currentOptions.every((currentOption, currentOptionIndex) => {
                if (!currentOption || optionIndex === currentOptionIndex) {
                  return true
                }

                return variant.selectedOptions[currentOptionIndex]?.value === currentOption
              })
            })
            .every((variant) => !variant.available)
        })

        /**
         * Return extended data.
         */
        return {
          ...option,
          disabled,
          values: getSortedOptionValues(option.values),
        }
      })
    },

    /**
     * Move current options to the next product if possible.
     */
    transitionCurrentOptions() {
      const newOptions = []
      this.product.options.forEach((newOpt) => {
        const oldOption = this.options.find((oldOpt) => {
          return oldOpt.name === newOpt.name
        })

        newOptions[newOpt.index] = ''

        if (!oldOption) {
          return
        }

        const existingValue = this.currentOptions[oldOption.index]

        if (newOpt.values.includes(existingValue)) {
          newOptions[newOpt.index] = existingValue
        }
      })

      this.currentOptions = newOptions
    },

    /**
     * Check if option should be hidden. The variant option would be hidden if
     * the name exists in `shop.metafields.global.color_options` and there is
     * only one option value.
     * @param {Object} option
     * @returns {Boolean}
     */
    isOptionHidden(option) {
      const colors = this.product?.colorOptions
      if (!colors) {
        return false
      }
      return colors.some((color) => {
        return color.index === option.index && option.values.length <= 1
      })
    },

    /**
     * Get the selection tab label HTML
     * @param option Variant option with name
     * @param value Variant value
     */
    getOptionValueLabel(option, value) {
      return `<span class="visually-hidden">${option.name}</span> ${value}`
    },

    /**
     * Get the option label that is visible to the client, but not screenreaders
     * @param option Variant option
     * @returns {string}
     */
    getOptionLabel(option) {
      let label = `${option.name}`
      if (this.scarcityMessage && option.name.toLowerCase().includes('size')) {
        label += `<span class="product-options__scarcity">: ${this.scarcityMessage}</span>`
      }
      return label.trim()
    },

    /**
     * Get the option label that is only visible to screenreaders.
     * @param option Variant option
     * @return {string}
     */
    getOptionLegend(option) {
      let label = `Select ${option.name}. `
      if (this.scarcityMessage && option.name.toLowerCase().includes('size')) {
        label += this.scarcityMessage
      }
      return label.trim()
    },
  },
}
</script>

<style lang="scss">
@import './product-options';
</style>
